import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tendenciasEnVivo, getTopPorCategoria, getTendenciasExplosivas, tiposTendencia, direccionConfig, reglasTendencias, statsTendencias, formatearNumero, type TipoTendencia, type TendenciaEnVivo } from '@/mocks/tendenciasEnVivo';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import ModalBase from '@/components/base/ModalBase';

function TendenciaDetailModal({ tendencia, onClose }: { tendencia: TendenciaEnVivo; onClose: () => void }) {
  const tipoInfo = tiposTendencia.find(t => t.id === tendencia.tipo);
  const dirInfo = direccionConfig[tendencia.direccion];

  return (
    <ModalBase onClose={onClose} maxWidth="max-w-lg">
      <div className="relative h-52 rounded-t-2xl overflow-hidden -mx-6 -mt-6">
        <img src={tendencia.imagenUrl} alt={tendencia.nombre} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-foreground-950/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-foreground-950/60 transition-colors cursor-pointer"
        >
          <i className="ri-close-line"></i>
        </button>
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${tipoInfo?.color} ${tipoInfo?.bg} bg-white/90`}>
            <i className={`${tipoInfo?.icono} text-[9px]`}></i>
            {tipoInfo?.label}
          </span>
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <h2 className="text-xl font-bold text-white drop-shadow-md">{tendencia.nombre}</h2>
          <p className="text-sm text-white/70">{tendencia.subtitulo}</p>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-background-100 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-foreground-950">#{tendencia.posicion}</p>
            <p className="text-[10px] text-foreground-500">Posición</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3 text-center">
            <p className={`text-lg font-bold ${dirInfo.textoColor}`}>+{tendencia.crecimiento}%</p>
            <p className="text-[10px] text-foreground-500">Crecimiento</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-foreground-950">{tendencia.tendenciaScore}</p>
            <p className="text-[10px] text-foreground-500">Score</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-foreground-950">{tendencia.cambioPosicion > 0 ? `+${tendencia.cambioPosicion}` : tendencia.cambioPosicion}</p>
            <p className="text-[10px] text-foreground-500">Cambio</p>
          </div>
        </div>

        {/* Señales de actividad */}
        <div>
          <h4 className="text-sm font-bold text-foreground-900 mb-3">Señales de Actividad</h4>
          <div className="grid grid-cols-2 gap-3">
            {tendencia.senalesActividad.oyentes > 0 && (
              <div className="flex items-center gap-3 bg-background-100 rounded-xl p-3">
                <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center">
                  <i className="ri-headphone-line text-rose-600"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground-900">{formatearNumero(tendencia.senalesActividad.oyentes)}</p>
                  <p className="text-[10px] text-foreground-500">Oyentes</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3 bg-background-100 rounded-xl p-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                <i className="ri-chat-1-line text-amber-600"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground-900">{formatearNumero(tendencia.senalesActividad.comentarios)}</p>
                <p className="text-[10px] text-foreground-500">Comentarios</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-background-100 rounded-xl p-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                <i className="ri-share-forward-line text-emerald-600"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground-900">{formatearNumero(tendencia.senalesActividad.compartidos)}</p>
                <p className="text-[10px] text-foreground-500">Compartidos</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-background-100 rounded-xl p-3">
              <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
                <i className="ri-heart-line text-violet-600"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground-900">{formatearNumero(tendencia.senalesActividad.interacciones)}</p>
                <p className="text-[10px] text-foreground-500">Interacciones</p>
              </div>
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="flex flex-wrap gap-4 text-xs text-foreground-600">
          <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i> {tendencia.ciudad}, {tendencia.pais}</span>
          <span className="flex items-center gap-1"><i className="ri-time-line"></i> {tendencia.horario}</span>
          {tendencia.categoria && <span className="flex items-center gap-1"><i className="ri-price-tag-3-line"></i> {tendencia.categoria}</span>}
        </div>

        {tendencia.badge && (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold">
            <i className="ri-award-line"></i> {tendencia.badge}
          </span>
        )}
      </div>
    </ModalBase>
  );
}

export default function TendenciasEnVivoPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TipoTendencia | 'todas' | 'reglas'>('todas');
  const [selectedTendencia, setSelectedTendencia] = useState<TendenciaEnVivo | null>(null);
  const [filtroDireccion, setFiltroDireccion] = useState<'todas' | 'explosivo' | 'subiendo'>('todas');

  const filtered = (() => {
    let items = activeTab === 'todas' ? tendenciasEnVivo : activeTab === 'reglas' ? [] : tendenciasEnVivo.filter(t => t.tipo === activeTab);
    if (filtroDireccion === 'explosivo') items = items.filter(t => t.direccion === 'explosivo');
    if (filtroDireccion === 'subiendo') items = items.filter(t => t.direccion === 'explosivo' || t.direccion === 'subiendo');
    return items;
  })();

  const explosivas = getTendenciasExplosivas();
  const topPorCat = getTopPorCategoria();

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative h-[420px] md:h-[500px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Dynamic%20real-time%20data%20visualization%20dashboard%20with%20glowing%20trending%20charts%20and%20heat%20maps%2C%20dark%20sophisticated%20analytics%20center%20with%20warm%20amber%20and%20rose%20accent%20lights%2C%20modern%20broadcast%20control%20room%20aesthetic%2C%20futuristic%20yet%20warm%20atmosphere%2C%20professional%20data%20analytics%20photography%20with%20shallow%20depth%20of%20field&width=1600&height=900&seq=tendencias-hero-59&orientation=landscape"
          alt="Tendencias en Vivo RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/30 to-foreground-950/70"></div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/90 text-white text-xs font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
            TENDENCIAS EN VIVO
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3 drop-shadow-lg">Lo Que Está Pasando Ahora</h1>
          <p className="text-base md:text-lg text-white/70 max-w-xl">Tendencias calculadas en tiempo real basadas en actividad de la comunidad. Sin intervención manual.</p>

          <div className="flex items-center gap-6 mt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{statsTendencias.totalTendencias}</p>
              <p className="text-[10px] text-white/50">Tendencias</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-rose-300">{statsTendencias.tendenciasExplosivas}</p>
              <p className="text-[10px] text-white/50">Explosivas</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{statsTendencias.categoriasActivas}</p>
              <p className="text-[10px] text-white/50">Categorías</p>
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate('/')}
          className="absolute top-6 left-6 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm text-white text-sm font-semibold hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          RFW
        </button>
      </section>

      {/* Stats bar sticky */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-xl border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i> RFW
          </button>
          <div className="flex items-center gap-4 md:gap-6 text-xs">
            <span className="flex items-center gap-1 text-foreground-500">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span>
              {statsTendencias.actualizacionesUltimaHora} actualizaciones/h
            </span>
            <span className="hidden sm:inline text-foreground-500">
              <span className="font-semibold text-foreground-700">{statsTendencias.totalTendencias}</span> tendencias
            </span>
            <span className="hidden md:inline text-foreground-500">
              Más caliente: <span className="font-semibold text-rose-600">{statsTendencias.categoriaMasCaliente}</span>
            </span>
            <button
              onClick={() => setActiveTab(activeTab === 'reglas' ? 'todas' : 'reglas')}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={`${activeTab === 'reglas' ? 'ri-fire-line' : 'ri-scales-line'} text-[10px]`}></i>
              {activeTab === 'reglas' ? 'Ver Tendencias' : 'Reglas'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: 'todas' as const, label: 'Todas', icono: 'ri-fire-line', count: tendenciasEnVivo.length },
            ...tiposTendencia.map(t => ({ id: t.id, label: t.label, icono: t.icono, count: tendenciasEnVivo.filter(x => x.tipo === t.id).length })),
            { id: 'reglas' as const, label: 'Reglas', icono: 'ri-scales-line', count: reglasTendencias.length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setFiltroDireccion('todas'); }}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                activeTab === tab.id
                  ? 'bg-rose-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
              }`}
            >
              <i className={`${tab.icono} text-xs`}></i>
              {tab.label}
              <span className="text-[10px] opacity-70">({tab.count})</span>
            </button>
          ))}
        </div>

        {/* Filtro de dirección — solo cuando no es reglas */}
        {activeTab !== 'reglas' && (
          <div className="flex items-center gap-2 mb-6">
            <span className="text-xs text-foreground-500">Filtrar:</span>
            {[
              { id: 'todas' as const, label: 'Todas' },
              { id: 'explosivo' as const, label: 'Explosivas', icono: 'ri-fire-line' },
              { id: 'subiendo' as const, label: 'En Ascenso', icono: 'ri-arrow-up-line' },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFiltroDireccion(f.id)}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  filtroDireccion === f.id
                    ? 'bg-rose-100 text-rose-700'
                    : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'
                }`}
              >
                {f.icono && <i className={`${f.icono} text-[9px]`}></i>}
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Contenido */}
        {activeTab !== 'reglas' ? (
          <>
            {/* Top por categoría */}
            {activeTab === 'todas' && topPorCat.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                {topPorCat.map(({ tipo, item }) => {
                  const tipoInfo = tiposTendencia.find(t => t.id === tipo);
                  return (
                    <button
                      key={tipo}
                      onClick={() => setActiveTab(tipo)}
                      className="group relative h-44 rounded-2xl overflow-hidden cursor-pointer"
                    >
                      <img src={item.imagenUrl} alt={item.nombre} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>
                      <div className="absolute top-3 left-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border ${tipoInfo?.color} ${tipoInfo?.bg} bg-white/90`}>
                          <i className={`${tipoInfo?.icono} text-[8px]`}></i>
                          {tipoInfo?.label}
                        </span>
                      </div>
                      <div className="absolute bottom-3 left-3 right-3">
                        <p className="text-sm font-bold text-white truncate drop-shadow-md">{item.nombre}</p>
                        <p className="text-[10px] text-white/60 truncate">{item.subtitulo}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Grid principal */}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filtered.map(tendencia => {
                  const tipoInfo = tiposTendencia.find(t => t.id === tendencia.tipo);
                  const dirInfo = direccionConfig[tendencia.direccion];
                  return (
                    <div
                      key={tendencia.id}
                      onClick={() => setSelectedTendencia(tendencia)}
                      className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-background-300/60 transition-all cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img src={tendencia.imagenUrl} alt={tendencia.nombre} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
                        <div className="absolute top-3 left-3 flex items-center gap-1.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${tipoInfo?.color} ${tipoInfo?.bg} bg-white/90`}>
                            <i className={`${tipoInfo?.icono} text-[9px]`}></i>
                            {tipoInfo?.label}
                          </span>
                          {tendencia.direccion === 'explosivo' && (
                            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-bold">
                              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                              EXPLOSIVO
                            </span>
                          )}
                        </div>
                        <div className="absolute top-3 right-3">
                          <span className="px-2 py-0.5 rounded-md bg-foreground-950/60 backdrop-blur-sm text-white text-[10px] font-bold">#{tendencia.posicion}</span>
                        </div>
                        <div className="absolute bottom-3 left-3 right-3">
                          <h3 className="text-sm font-bold text-white truncate drop-shadow-md">{tendencia.nombre}</h3>
                        </div>
                      </div>
                      <div className="p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <img src={tendencia.logoUrl} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-foreground-700 truncate">{tendencia.subtitulo}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${dirInfo.textoColor}`}>
                                <i className={`${dirInfo.icono} text-[9px]`}></i>
                                {dirInfo.label}
                              </span>
                              <span className="text-[10px] text-foreground-500">+{tendencia.crecimiento}%</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-[10px] text-foreground-500">
                          {tendencia.senalesActividad.oyentes > 0 && (
                            <span className="flex items-center gap-1"><i className="ri-headphone-line text-[9px]"></i>{formatearNumero(tendencia.senalesActividad.oyentes)}</span>
                          )}
                          <span className="flex items-center gap-1"><i className="ri-chat-1-line text-[9px]"></i>{formatearNumero(tendencia.senalesActividad.comentarios)}</span>
                          <span className="flex items-center gap-1"><i className="ri-share-forward-line text-[9px]"></i>{formatearNumero(tendencia.senalesActividad.compartidos)}</span>
                        </div>
                        {tendencia.badge && (
                          <span className="inline-block px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[9px] font-semibold">{tendencia.badge}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                icon="ri-fire-line"
                title="Sin resultados"
                description="No hay tendencias que coincidan con los filtros actuales."
                actionLabel="Limpiar filtros"
                onAction={() => { setActiveTab('todas'); setFiltroDireccion('todas'); }}
              />
            )}
          </>
        ) : (
          /* Vista Reglas */
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {reglasTendencias.map(regla => (
                <div key={regla.id} className={`bg-background-50 rounded-2xl border ${regla.colorBorde} p-6 space-y-3`}>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${regla.gradiente} flex items-center justify-center`}>
                    <i className={`${regla.icono} text-white text-xl`}></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-900">{regla.titulo}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{regla.descripcion}</p>
                </div>
              ))}
            </div>

            {/* Score summary */}
            <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6">
              <h3 className="text-base font-bold text-foreground-900 mb-4">Resumen de Tendencias</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground-950">{statsTendencias.totalTendencias}</p>
                  <p className="text-xs text-foreground-500">Tendencias Totales</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-rose-600">{statsTendencias.tendenciasExplosivas}</p>
                  <p className="text-xs text-foreground-500">Explosivas</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{statsTendencias.actualizacionesUltimaHora}</p>
                  <p className="text-xs text-foreground-500">Updates/h</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{statsTendencias.categoriaMasCaliente}</p>
                  <p className="text-xs text-foreground-500">Más Caliente</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-violet-600">{statsTendencias.itemTop}</p>
                  <p className="text-xs text-foreground-500">#1 Global</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 p-8 rounded-3xl bg-gradient-to-r from-rose-500/10 via-amber-500/10 to-rose-500/10 border border-rose-200/30 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center mb-4">
            <i className="ri-fire-line text-white text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-foreground-900 mb-2">¿Querés estar en tendencia?</h3>
          <p className="text-sm text-foreground-600 max-w-md mx-auto mb-5">Creá contenido de calidad, interactuá con tu comunidad, hacé eventos y concursos. La actividad real es lo que te lleva al top.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => navigate('/register-radio')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 text-white font-bold text-sm hover:from-rose-600 hover:to-amber-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-radio-line"></i>
              Crear mi Radio
            </button>
            <button
              onClick={() => navigate('/tv-app')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-background-100 text-foreground-700 font-semibold text-sm hover:bg-background-200/70 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-tv-line"></i>
              Ver en TV App
            </button>
          </div>
        </div>

        {/* Regla final */}
        <div className="mt-8 text-center">
          <p className="text-xs text-foreground-400 italic">"Basado en actividad real, no manual. La comunidad define qué es tendencia."</p>
        </div>
      </div>

      {/* Modal de detalle */}
      {selectedTendencia && (
        <TendenciaDetailModal
          tendencia={selectedTendencia}
          onClose={() => setSelectedTendencia(null)}
        />
      )}
    </div>
  );
}